import React from 'react';

function Barometer({ 
  quarterlyData,
  liggetidData,
  afslagData,
  forSaleData,
  bnpData,
  monthlyData,
  calculateQuarterlyAverageRente,
  calculateYearOverYearInflation,
  calculateQuarterlyAverageUnemployment
}) {
  // Quarter weights: most recent = 40%, then 30%, 20%, 10%
  const QUARTER_WEIGHTS = [0.4, 0.3, 0.2, 0.1];

  const parseQuarterString = (quarterStr) => {
    const year = parseInt(quarterStr.slice(0, 4));
    const quarter = parseInt(quarterStr.slice(-1));
    return { year, quarter };
  };

  const calculateYearOverYearChange = (data, quarter) => {
    const { year, quarter: qNumber } = parseQuarterString(quarter);
    const prevYearQuarter = `${year - 1}K${qNumber}`;
    
    const currentValue = data[quarter];
    const prevValue = data[prevYearQuarter];
    
    if (currentValue == null || prevValue == null) return null;
    if (prevValue === 0) return null; // Avoid division by zero
    return ((currentValue - prevValue) / prevValue) * 100;
  };

  // Scoring functions
  const getScore = (data, quarter, type) => {
    if (!data || data[quarter] == null) return 0;
    
    const value = type === 'forSale' 
      ? calculateYearOverYearChange(data, quarter)
      : data[quarter];

    if (value == null) return 0;

    switch(type) {
      // For salgspris & boligsalg:
      // Example thresholds used here; adjust as per your final logic
      case 'salgspris':
      case 'boligsalg':
        if (value > 5) return 3;
        if (value > 2) return 2;
        if (value > -2) return 1;
        return 0;
      
      // For boliger til salg & liggetid:
      case 'forSale':
      case 'liggetid':
        if (value <= -5) return 3;
        if (value < -2) return 2;
        if (value < 2) return 1;
        return 0;
      
      default:
        return 0;
    }
  };

  const getRenteScore = (quarter) => {
    const value = calculateQuarterlyAverageRente(quarter);
    if (value == null) return 0;
    
    if (value <= 2.0) return 3;
    if (value <= 4.0) return 2;
    if (value <= 6.0) return 1;
    return 0;
  };

  const getAfslagScore = (data, quarter) => {
    const value = data[quarter];
    if (value == null) return 0;
    
    if (value <= 3.5) return 3;
    if (value <= 5.0) return 2;
    if (value <= 7.9) return 1;
    return 0;
  };

  const getBNPScore = (data, quarter) => {
    const value = data[quarter];
    if (value == null) return 0;
    
    if (value >= 2.0) return 3;
    if (value >= 1.0) return 2;
    if (value >= 0.0) return 1;
    return 0;
  };

  const getInflationScore = (quarter) => {
    const value = calculateYearOverYearInflation(quarter);
    if (value == null) return 0;
    
    if (value <= 2.0) return 3;
    if (value <= 3.0) return 2;
    if (value <= 4.0) return 1;
    return 0;
  };

  const getLedighedScore = (quarter) => {
    const value = calculateQuarterlyAverageUnemployment(quarter);
    if (value == null) return 0;
    
    if (value <= 3.0) return 3;
    if (value <= 4.0) return 2;
    if (value <= 5.0) return 1;
    return 0;
  };

  const haveCompleteDataForQuarter = (quarter) => {
    return quarterlyData.boligpris && quarterlyData.boligpris[quarter] != null &&
           quarterlyData.boligsalg && quarterlyData.boligsalg[quarter] != null &&
           calculateQuarterlyAverageRente(quarter) != null &&
           forSaleData && forSaleData[quarter] != null &&
           afslagData && afslagData[quarter] != null &&
           liggetidData && liggetidData[quarter] != null &&
           bnpData && bnpData[quarter] != null &&
           calculateYearOverYearInflation(quarter) != null &&
           calculateQuarterlyAverageUnemployment(quarter) != null;
  };

  const findLastFourQuarters = () => {
    const quarters = Object.keys(quarterlyData.boligpris || {});
    // Sort quarters in chronological order: e.g. "2020K1", "2020K2", ...
    // A simple string sort works if formatted consistently as YYYYKQ
    const sortedQuarters = quarters.sort();

    // Filter quarters that have complete data
    const completeQuarters = sortedQuarters.filter(haveCompleteDataForQuarter);

    // Get the last 4 quarters of complete data
    return completeQuarters.slice(-4);
  };

  const calculateBarometerScore = () => {
    const selectedQuarters = findLastFourQuarters();
    if (selectedQuarters.length === 0) return null;

    // If fewer than 4 quarters available, we still distribute weights proportionally
    // e.g. if only 2 quarters are available, we could do 70% (for the last) and 30% (for the second last)
    // But the PRD suggests using 4 quarters. For safety, we scale weights if less than 4:
    const weightsToUse = QUARTER_WEIGHTS.slice(-selectedQuarters.length);
    const weightSum = weightsToUse.reduce((a,b) => a+b,0);
    const normalizedWeights = weightsToUse.map(w => w / weightSum);

    let totalWeightedScore = 0;

    // Maximum possible score for one quarter is always the same as the calculation for a perfect scenario:
    // Each metric can yield a max of 3 points. With given weights:
    // sum of weights = 1.0 total
    // 3 points * weight_of_metric, summed over all metrics, equals 3.0 * 1.0 = 3 points max per quarter
    // But since we will sum over multiple quarters with quarter weights that sum to 1.0,
    // the overall max remains the same as if just one quarter at full score. (3 points * total data point weights * total quarter weights = 3 * 1 * 1 = 3)
    // After converting to percentage we use totalWeightedScore / maxPossibleScore * 100
    // maxPossibleScore for the entire calculation remains 3.0 * 100% = 300% if we think in old terms, but since we ended up normalizing to a final percentage:
    // The max would be (3 * sum_of_data_point_weights * sum_of_quarter_weights) = (3 * 1.0 * 1.0) = 3 points in total normalized scale.
    // We will just keep using the same logic at the end.

    selectedQuarters.forEach((q, index) => {
      const quarterWeight = normalizedWeights[index];

      // Get raw scores (0-3) for each metric for this quarter
      const scores = {
        salgspris: getScore(quarterlyData.boligpris, q, 'salgspris'),
        boligsalg: getScore(quarterlyData.boligsalg, q, 'boligsalg'),
        rente: getRenteScore(q),
        boligerTilSalg: getScore(forSaleData, q, 'forSale'),
        prisafslag: getAfslagScore(afslagData, q),
        liggetid: getScore(liggetidData, q, 'liggetid'),
        bnp: getBNPScore(bnpData, q),
        inflation: getInflationScore(q),
        ledighed: getLedighedScore(q)
      };

      // Apply data point weights
      const weightedScores = {
        salgspris: scores.salgspris * 0.25,
        boligsalg: scores.boligsalg * 0.125,
        rente: scores.rente * 0.25,
        boligerTilSalg: scores.boligerTilSalg * 0.05,
        prisafslag: scores.prisafslag * 0.15,
        liggetid: scores.liggetid * 0.05,
        bnp: scores.bnp * 0.05,
        inflation: scores.inflation * 0.05,
        ledighed: scores.ledighed * 0.025
      };

      // Sum for this quarter
      const quarterScore = Object.values(weightedScores).reduce((a, b) => a + b, 0);

      // Multiply by quarter weight and add to total
      totalWeightedScore += quarterScore * quarterWeight;
    });

    // Maximum possible score:
    // Each metric can score max 3, and total metric weights sum to 1.0:
    // max per quarter = 3 * 1.0 = 3
    // Applying quarter weights (which sum to 1.0) still results in a max total of 3.
    const maxPossibleScore = 3; 

    return (totalWeightedScore / maxPossibleScore) * 100;
  };

  const score = calculateBarometerScore();

  const getMarketInterpretation = (score) => {
    if (score === null) return null;

    if (score < 25) {
      return "Markedet er præget af længere salgstider og større prisforhandlinger. Købere har en stærk position i markedet.";
    } else if (score < 45) {
      return "Markedet hælder mod køberne. Der er gode muligheder for prisforhandling, og købere har generelt en fordelagtig position.";
    } else if (score < 55) {
      return "Boligmarkedet er i balance mellem købere og sælgere. Priserne er stabile, og der er rimelig aktivitet i markedet.";
    } else if (score < 75) {
      return "Markedet favoriserer sælgere. Der er god efterspørgsel, kortere liggetider, og mindre rum for prisforhandling.";
    } else {
      return "Markedet er karakteriseret ved høj efterspørgsel, hurtige salg og stigende priser. Sælgere har en meget stærk position.";
    }
  };

  return (
    <div className="barometer">
      <h1>Boligbarometer</h1>
      <div className="barometer-display">
        <div className="progress-bar-container">
          <div className="progress-bar">
            <span className="bar-text-left">😭 Lave priser</span>
            <span className="bar-text-right">Høje priser 🤑</span>
          </div>
          {score !== null && (
            <>
              <div 
                className="score-indicator"
                style={{ 
                  '--target-position': `${Math.round(score)}%`
                }}
              >
                {`${Math.round(score)}%`}
              </div>
              <div 
                className="dynamic-line"
                style={{ 
                  '--target-position': `${Math.round(score)}%`,
                  backgroundColor: getDarkerColor(score)
                }}
              />
            </>
          )}
        </div>
        
        {score !== null && (
          <p className="interpretation">
            {getMarketInterpretation(score)}
          </p>
        )}
      </div>
    </div>
  );
}

// Function to calculate a darker color based on the score
function getDarkerColor(score) {
  const red = Math.round(198 - (score / 100) * (198 - 46));
  const green = Math.round(40 + (score / 100) * (125 - 40));
  const blue = Math.round(40 + (score / 100) * (50 - 40));
  return `rgba(${red}, ${green}, ${blue}, 0.15)`; // 40% darker
}

export default Barometer;
